"use client";

import { useState } from "react";

import Button from "../atoms/button";
import Icon from "../atoms/icon";
import View from "../atoms/view";

/**
 * @name StarRating
 * @description Renders an interactive star rating component.
 *
 * @param {Object} props - Component props.
 * @param {number} [props.totalStars=5] - The total number of stars to display.
 * @param {number} [props.initialRating=4] - The initial rating value.
 * @param {(rating: number) => void} [props.onChange] - Callback function when the rating changes.
 * @returns {JSX.Element} The rendered star rating component.
 */

export interface StarRatingProps {
  totalStars?: number;
  initialRating?: number;
  onChange?: (rating: number) => void;
}
const StarRating = ({
  totalStars = 5,
  initialRating = 4,
  onChange
}: StarRatingProps) => {
  const [rating, setRating] = useState(initialRating);

  const handleStarClick = (clickedRating: number) => {
    setRating(clickedRating);
    if (onChange) {
      onChange(clickedRating);
    }
  };

  return (
    <View className="flex gap-1 text-primary">
      {Array.from({ length: totalStars }).map((_, index) => {
        const starValue = index + 1;

        let IconComponent: React.JSX.Element;
        if (starValue <= rating) {
          IconComponent = <Icon name="fa-solid fa-star" />;
        } else {
          IconComponent = <Icon name="fa-regular fa-star" />;
        }

        return (
          <Button
            key={starValue}
            className="cursor-pointer"
            action={() => handleStarClick(starValue)}
          >
            {IconComponent}
          </Button>
        );
      })}
    </View>
  );
};

export default StarRating;
